import { createLogger } from '@/core/helpers/logger.helper';
import { onMounted, onUnmounted, ref } from 'vue';

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const CURRENT_SHA = import.meta.env.VITE_COMMIT_SHA;

const logger = createLogger('AppUpdate');

interface VersionPayload {
  commitSha?: string;
  forceRefresh: boolean;
}

async function fetchVersionInfo(): Promise<VersionPayload | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as VersionPayload;
  } catch {
    return null;
  }
}

export function useAppUpdate() {
  const updateAvailable = ref(false);
  const isReloading = ref(false);

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let dismissed = false;

  async function checkForUpdate() {
    if (dismissed || updateAvailable.value) return;

    const payload = await fetchVersionInfo();
    if (!payload) return;

    const isNewBuild = !!payload.commitSha && payload.commitSha !== CURRENT_SHA;
    if (isNewBuild && payload.forceRefresh) {
      logger.info(`Force refresh required: ${CURRENT_SHA} → ${payload.commitSha}`);
      updateAvailable.value = true;
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  }

  async function reload() {
    isReloading.value = true;
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      // Ignore cleanup errors
    }

    window.location.reload();
  }

  function dismiss() {
    dismissed = true;
    updateAvailable.value = false;
  }

  onMounted(() => {
    checkForUpdate();
    pollTimer = setInterval(checkForUpdate, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onUnmounted(() => {
    if (pollTimer) clearInterval(pollTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return { updateAvailable, reload, dismiss, isReloading };
}
